/**
 * Remotion Slide Composition
 *
 * Main composition that renders a slide/frame with all overlays.
 * This is used by both the Player (preview) and renderStill (export).
 *
 * Compatible with your existing data model from Post.ts.
 */

import React from 'react';
import { AbsoluteFill, Img, useCurrentFrame } from 'remotion';
import {
  AnyOverlay,
  TextOverlay,
  ImageOverlay,
  SvgOverlay,
  OverlayAnimation,
  ImageFilter,
} from '../../models/Post';
import { resolveImageFilterCss } from '../../utils/imageFilter';
import { RemotionTextOverlay } from './RemotionTextOverlay';
import { RemotionImageOverlay } from './RemotionImageOverlay';
import { RemotionSvgOverlay } from './RemotionSvgOverlay';

export interface RemotionSlideProps {
  [key: string]: unknown; // Index signature for Remotion Player compatibility
  width: number;
  height: number;
  backgroundColor?: string;
  backgroundImageUrl?: string;
  backgroundFilter?: ImageFilter;
  overlays: AnyOverlay[];
  /** Map of overlay filePath -> signed URL (for images) */
  imageUrls?: Record<string, string>;
  /** Current time in seconds (for video mode, otherwise 0) */
  currentTimeSeconds?: number;
  /** Whether this is video mode (enables timing/animation) */
  isVideo?: boolean;
  /** Video duration in seconds */
  videoDuration?: number;
  /** Frames per second */
  fps?: number;
  /** Reference media width for scaling (original/natural width) */
  referenceWidth?: number;
  /** Reference media height for scaling (original/natural height) */
  referenceHeight?: number;
  /** Layout engine: 'fabric' (old posts) applies CSS compensations, 'remotion' (new posts) uses clean CSS */
  layoutEngine?: 'fabric' | 'remotion';
}

/**
 * Type guards for overlay types
 */
function isTextOverlay(overlay: AnyOverlay): overlay is TextOverlay {
  return overlay.type === 'text' || overlay.type === undefined;
}

function isImageOverlay(overlay: AnyOverlay): overlay is ImageOverlay {
  return overlay.type === 'image';
}

function isSvgOverlay(overlay: AnyOverlay): overlay is SvgOverlay {
  return overlay.type === 'svg';
}

/**
 * Calculate animation state for an overlay
 */
function calculateAnimationOpacity(
  animation: OverlayAnimation | undefined,
  startTime: number,
  currentTime: number
): number {
  if (!animation) return 1;

  const duration = animation.duration ?? 1.0;
  const elapsed = currentTime - startTime;

  if (elapsed < 0) return animation.type === 'fadeIn' ? 0 : 1;
  if (elapsed >= duration) return 1;

  const progress = elapsed / duration;

  // Apply easing
  let easedProgress: number;
  switch (animation.easing ?? 'easeOut') {
    case 'linear':
      easedProgress = progress;
      break;
    case 'easeIn':
      easedProgress = progress * progress * progress;
      break;
    case 'easeOut':
      easedProgress = 1 - Math.pow(1 - progress, 3);
      break;
    case 'easeInOut':
      easedProgress = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      break;
    default:
      easedProgress = 1 - Math.pow(1 - progress, 3);
  }

  if (animation.type === 'fadeIn') {
    return easedProgress;
  }

  return 1;
}

/**
 * Calculate slide animation offset
 */
function calculateSlideOffset(
  animation: OverlayAnimation | undefined,
  startTime: number,
  currentTime: number,
  containerWidth: number,
  containerHeight: number,
  overlayWidth: number
): { x: number; y: number } {
  if (!animation || animation.type !== 'slideIn') {
    return { x: 0, y: 0 };
  }

  const duration = animation.duration ?? 1.0;
  const elapsed = currentTime - startTime;
  const direction = animation.direction ?? 'left';

  // Calculate start offset based on direction
  let startX = 0;
  let startY = 0;
  switch (direction) {
    case 'left':
      startX = -overlayWidth - 100;
      break;
    case 'right':
      startX = containerWidth + 100;
      break;
    case 'top':
      startY = -containerHeight;
      break;
    case 'bottom':
      startY = containerHeight + 100;
      break;
  }

  if (elapsed < 0) return { x: startX, y: startY };
  if (elapsed >= duration) return { x: 0, y: 0 };

  const progress = elapsed / duration;

  // Apply easing
  let easedProgress: number;
  switch (animation.easing ?? 'easeOut') {
    case 'linear':
      easedProgress = progress;
      break;
    case 'easeIn':
      easedProgress = progress * progress * progress;
      break;
    case 'easeOut':
      easedProgress = 1 - Math.pow(1 - progress, 3);
      break;
    case 'easeInOut':
      easedProgress = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      break;
    default:
      easedProgress = 1 - Math.pow(1 - progress, 3);
  }

  return {
    x: startX * (1 - easedProgress),
    y: startY * (1 - easedProgress),
  };
}

export const RemotionSlide: React.FC<RemotionSlideProps> = ({
  width,
  height,
  backgroundColor = '#000000',
  backgroundImageUrl,
  backgroundFilter,
  overlays,
  imageUrls = {},
  currentTimeSeconds = 0,
  isVideo = false,
  videoDuration = 0,
  fps = 30,
  referenceWidth,
  referenceHeight,
  layoutEngine,
}) => {
  // For video mode, use Remotion's frame system
  const frame = useCurrentFrame();
  const currentTime = isVideo ? frame / fps : currentTimeSeconds;

  // Calculate scale factor: how much we're scaling from reference to actual display size
  // This matches how Fabric.js calculates: scaleFactor = canvasWidth / referenceMediaWidth
  const scaleFactor = referenceWidth && referenceWidth > 0 ? width / referenceWidth : 1;

  // Filter overlays by timing (for video mode)
  const visibleOverlays = overlays.filter((overlay) => {
    if (!isVideo || !overlay.timing) return true;
    return currentTime >= overlay.timing.startTime && currentTime <= overlay.timing.endTime;
  });

  // Sort by zIndex
  const sortedOverlays = [...visibleOverlays].sort(
    (a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0)
  );

  return (
    <AbsoluteFill style={{ backgroundColor }}>
      {/* Background image */}
      {backgroundImageUrl && (
        <Img
          src={backgroundImageUrl}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: resolveImageFilterCss(backgroundFilter),
          }}
        />
      )}

      {/* Render all overlays */}
      {sortedOverlays.map((overlay) => (
        <OverlayRenderer
          key={overlay.id}
          overlay={overlay}
          containerWidth={width}
          containerHeight={height}
          imageUrls={imageUrls}
          currentTime={currentTime}
          isVideo={isVideo}
          scaleFactor={scaleFactor}
          layoutEngine={layoutEngine}
        />
      ))}
    </AbsoluteFill>
  );
};

/**
 * Individual overlay renderer with animation support
 */
interface OverlayRendererProps {
  overlay: AnyOverlay;
  containerWidth: number;
  containerHeight: number;
  imageUrls: Record<string, string>;
  currentTime: number;
  isVideo: boolean;
  scaleFactor: number;
  layoutEngine?: 'fabric' | 'remotion';
}

const OverlayRenderer: React.FC<OverlayRendererProps> = ({
  overlay,
  containerWidth,
  containerHeight,
  imageUrls,
  currentTime,
  isVideo,
  scaleFactor,
  layoutEngine,
}) => {
  // Calculate animation state
  const startTime = overlay.timing?.startTime ?? 0;
  const animationOpacity = isVideo
    ? calculateAnimationOpacity(overlay.animation, startTime, currentTime)
    : 1;

  const slideOffset = isVideo
    ? calculateSlideOffset(
        overlay.animation,
        startTime,
        currentTime,
        containerWidth,
        containerHeight,
        (overlay.width ?? 50) / 100 * containerWidth
      )
    : { x: 0, y: 0 };

  // Wrapper for animation
  const animationStyle: React.CSSProperties = {
    opacity: animationOpacity,
    transform: slideOffset.x !== 0 || slideOffset.y !== 0
      ? `translate(${slideOffset.x}px, ${slideOffset.y}px)`
      : undefined,
  };

  if (isTextOverlay(overlay)) {
    return (
      <div style={animationStyle}>
        <RemotionTextOverlay
          overlay={overlay}
          containerWidth={containerWidth}
          containerHeight={containerHeight}
          scaleFactor={scaleFactor}
          layoutEngine={layoutEngine}
        />
      </div>
    );
  }

  if (isImageOverlay(overlay)) {
    // Look up preview URL by filePath first, then by overlay.id (for pending overlays)
    const imageUrl = imageUrls[overlay.filePath] || imageUrls[overlay.id] || overlay.filePath;
    return (
      <div style={animationStyle}>
        <RemotionImageOverlay
          overlay={overlay}
          containerWidth={containerWidth}
          containerHeight={containerHeight}
          imageUrl={imageUrl}
          scaleFactor={scaleFactor}
          layoutEngine={layoutEngine}
        />
      </div>
    );
  }

  if (isSvgOverlay(overlay)) {
    return (
      <div style={animationStyle}>
        <RemotionSvgOverlay
          overlay={overlay}
          containerWidth={containerWidth}
          containerHeight={containerHeight}
          scaleFactor={scaleFactor}
          layoutEngine={layoutEngine}
        />
      </div>
    );
  }

  return null;
};

export default RemotionSlide;
